import Phaser from "phaser";
import { unlockAudio } from "../audio/sfx";
import { setRaidAiSave } from "../data/raid-catalog";
import { t } from "../data/strings";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  finishSimRun,
  liveMetricToken,
  prepareSimRun,
  stepSimRun,
  type SimRunContext,
} from "../features/raid/sim-lab/sim-lab-runner";
import { compareReports } from "../features/raid/sim-lab/sim-lab-compare";
import type { SimReport, SimStageDef } from "../features/raid/sim-lab/sim-lab-types";
import {
  reduceSimTransport,
  simTransportClockRunning,
  simTransportPlayNeedsCue,
  type SimTransportPhase,
} from "../features/raid/sim-lab/sim-lab-transport";
import { clearSimMapOverride } from "../features/raid/sim-lab/sim-stage";
import {
  cssRectToGameViewport,
  fitContentInViewport,
} from "../features/raid/sim-lab/sim-lab-view";
import { drainEvents } from "../features/raid/raid-rules";
import { RaidPresentation } from "../features/raid/view/raid-presentation";
import { sharedInput } from "../input/input-controller";
import {
  behaviorLabel,
  mountRaidSimHud,
  type RaidSimHudApi,
  type SimCueRequest,
} from "../ui/huds/raid/sim-lab";
import { tryGetUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";
import { formatDisplay } from "../view/display-format";

type LaneTag = "run" | "A" | "B";

type PlaybackLane = {
  tag: LaneTag;
  ctx: SimRunContext;
  view: RaidPresentation;
  done: boolean;
  report: SimReport | null;
};

const DEFAULT_GAME_W = 540;
const DEFAULT_GAME_H = 960;

/**
 * Raid Sim Lab — Hub → Sim.
 * Deck transport: cue→ready paused → Play/Pause/Stop/Reset.
 * Compare = full-stage×2 canvas (no half-crop); shared transport drives both.
 */
export class RaidSimScene extends Phaser.Scene {
  private disposed = false;
  private hud: RaidSimHudApi | null = null;
  private lanes: PlaybackLane[] = [];
  private transport: SimTransportPhase = "ready";
  private compareMode = false;
  private camB: Phaser.Cameras.Scene2D.Camera | null = null;
  private laneLabels: Phaser.GameObjects.Text[] = [];
  private divider: Phaser.GameObjects.Rectangle | null = null;
  private readMarkers: Phaser.GameObjects.GameObject[] = [];
  private activeStage: SimStageDef | null = null;
  private compareOffsetX = 0;
  private frameRefreshQueued = false;

  constructor() {
    super(SceneKey.RaidSim);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.RaidSim);
    sharedInput.attach();
    clearOverlay();

    this.cameras.main.setBackgroundColor("#0c1014");

    this.hud = mountRaidSimHud({
      onToRoster: () => {
        unlockAudio();
        this.abortPlayback();
        assertTransition(SceneKey.RaidSim, SceneKey.RaidRoster);
        this.scene.start(SceneKey.RaidRoster);
      },
      onBack: () => this.backToHub(),
      onCue: (req) => this.cueReady(req),
      onPlay: (req) => this.handlePlay(req),
      onPause: () => this.handlePause(),
      onStop: (req) => this.cueReady(req),
      onReset: (req) => this.cueReady(req),
    });

    this.scale.on("resize", this.onScaleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      this.backToHub();
      return;
    }

    if (!simTransportClockRunning(this.transport) || this.lanes.length === 0) {
      return;
    }

    const dt = Math.min(0.05, dtMs / 1000);
    let anyActive = false;
    for (const lane of this.lanes) {
      if (lane.done) continue;
      anyActive = true;
      const done = stepSimRun(lane.ctx, dt);
      const events = drainEvents(lane.ctx.state);
      lane.view.sync(lane.ctx.state, events);
      if (done) {
        lane.done = true;
        lane.report = finishSimRun(lane.ctx, {
          clearSave: false,
          clearMap: false,
        });
      }
    }

    this.pushLiveStatus();

    if (!anyActive || this.lanes.every((l) => l.done)) {
      this.handleComplete();
    }
  }

  private setTransport(phase: SimTransportPhase): void {
    this.transport = phase;
    this.hud?.setTransportPhase(phase);
  }

  private applyTransportEvent(
    event: "cue" | "play" | "pause" | "stop" | "reset" | "complete",
  ): void {
    this.setTransport(reduceSimTransport(this.transport, event));
  }

  private laneGap(): number {
    return 24;
  }

  private onScaleResize = (): void => {
    if (this.disposed || !this.activeStage) return;
    if (this.frameRefreshQueued) return;
    this.frameRefreshQueued = true;
    this.time.delayedCall(0, () => {
      this.frameRefreshQueued = false;
      if (this.disposed || !this.activeStage) return;
      this.frameCamerasToHudGap(this.activeStage, {
        compare: this.compareMode,
        offsetX: this.compareOffsetX,
      });
      tryGetUiLayout()?.sync();
    });
  };

  private applyGameSize(w: number, h: number): void {
    this.scale.setGameSize(w, h);
    tryGetUiLayout()?.sync();
  }

  /** Frame stage/playBounds inside `.raid-sim-stage-gap` (under chrome/lanes). */
  private frameCamerasToHudGap(
    stage: SimStageDef,
    opts?: { compare?: boolean; offsetX?: number },
  ): void {
    const compare = opts?.compare === true;
    const offsetX = opts?.offsetX ?? 0;
    const canvas = this.game.canvas;
    const gapEl = document.querySelector(
      ".raid-sim-stage-gap",
    ) as HTMLElement | null;
    const fitMode = stage.view?.fit ?? "playBounds";
    const padFrac = stage.view?.padFrac ?? 0.05;
    const content =
      fitMode === "stage"
        ? {
            w: stage.width,
            h: stage.height,
            cx: stage.width / 2,
            cy: stage.height / 2,
          }
        : {
            w: stage.playBounds.xMax - stage.playBounds.xMin,
            h: stage.playBounds.yMax - stage.playBounds.yMin,
            cx: (stage.playBounds.xMin + stage.playBounds.xMax) / 2,
            cy: (stage.playBounds.yMin + stage.playBounds.yMax) / 2,
          };

    const fallback = () => {
      this.cameras.main.setViewport(0, 0, this.scale.width, this.scale.height);
      this.cameras.main.setZoom(1);
      this.cameras.main.centerOn(content.cx, content.cy);
    };

    if (!canvas || !gapEl) {
      fallback();
      return;
    }
    const cRect = canvas.getBoundingClientRect();
    const gRect = gapEl.getBoundingClientRect();
    const gapVp = cssRectToGameViewport(
      {
        left: cRect.left,
        top: cRect.top,
        width: cRect.width,
        height: cRect.height,
      },
      {
        left: gRect.left,
        top: gRect.top,
        width: gRect.width,
        height: gRect.height,
      },
      this.scale.width,
      this.scale.height,
    );
    if (!gapVp) {
      fallback();
      return;
    }

    if (!compare) {
      const fit = fitContentInViewport(
        gapVp,
        content.w,
        content.h,
        content.cx,
        content.cy,
        padFrac,
      );
      this.cameras.main.setViewport(
        fit.viewport.x,
        fit.viewport.y,
        fit.viewport.width,
        fit.viewport.height,
      );
      this.cameras.main.setZoom(fit.zoom);
      this.cameras.main.centerOn(fit.centerX, fit.centerY);
      return;
    }

    const midGap = Math.min(12, gapVp.width * 0.02);
    const halfW = (gapVp.width - midGap) / 2;
    const leftVp = {
      x: gapVp.x,
      y: gapVp.y,
      width: halfW,
      height: gapVp.height,
    };
    const rightVp = {
      x: gapVp.x + halfW + midGap,
      y: gapVp.y,
      width: halfW,
      height: gapVp.height,
    };
    const fitA = fitContentInViewport(
      leftVp,
      content.w,
      content.h,
      content.cx,
      content.cy,
      padFrac,
    );
    const fitB = fitContentInViewport(
      rightVp,
      content.w,
      content.h,
      offsetX + content.cx,
      content.cy,
      padFrac,
    );
    this.cameras.main.setViewport(
      fitA.viewport.x,
      fitA.viewport.y,
      fitA.viewport.width,
      fitA.viewport.height,
    );
    this.cameras.main.setZoom(fitA.zoom);
    this.cameras.main.centerOn(fitA.centerX, fitA.centerY);
    if (this.camB) {
      this.camB.setViewport(
        fitB.viewport.x,
        fitB.viewport.y,
        fitB.viewport.width,
        fitB.viewport.height,
      );
      this.camB.setZoom(fitB.zoom);
      this.camB.centerOn(fitB.centerX, fitB.centerY);
    }
  }

  private restoreDefaultGameSize(): void {
    this.applyGameSize(DEFAULT_GAME_W, DEFAULT_GAME_H);
  }

  private clearReadMarkers(): void {
    for (const g of this.readMarkers) {
      try {
        g.destroy();
      } catch {
        /* ignore */
      }
    }
    this.readMarkers = [];
  }

  /** Ally park ring + guide line; grit bounds-center cross. */
  private addReadMarkers(
    ctx: SimRunContext,
    offsetX = 0,
    opts?: { hideFrom?: Phaser.Cameras.Scene2D.Camera | null },
  ): void {
    const g = this.add.graphics().setDepth(9_500);
    if (ctx.runtime.allyId != null) {
      const ally = ctx.state.units.find(
        (u) => u.id === ctx.runtime.allyId && u.hp > 0,
      );
      if (ally) {
        const ax = ally.x + offsetX;
        const ay = ally.y;
        g.lineStyle(3, 0x7ec8ff, 0.85);
        g.strokeCircle(ax, ay, 36);
        g.lineStyle(2, 0x7ec8ff, 0.45);
        g.strokeCircle(ax, ay, 52);
        const fx = ctx.focus.x + offsetX;
        const fy = ctx.focus.y;
        g.lineStyle(2, 0x7ec8ff, 0.5);
        g.lineBetween(fx, fy, ax, ay);
      }
    }
    this.readMarkers.push(g);
    opts?.hideFrom?.ignore(g);
  }

  private liveCaptionFor(lane: PlaybackLane): string {
    const base = t("raid.sim.liveCaption", {
      beh: behaviorLabel(lane.ctx.focus.brain.select.activeId),
      focus: lane.ctx.focus.defId,
    });
    const token = liveMetricToken(lane.ctx);
    return token ? `${base} · ${token}` : base;
  }

  /** Setup board from cue; leave paused ready (shared transport). */
  private cueReady(req: SimCueRequest): void {
    this.abortPlayback({ keepTransport: true });
    this.applyTransportEvent("cue");
    this.hud?.setOutcome({ reports: [] });

    try {
      if (req.mode === "compare") {
        this.mountCompare(req);
      } else {
        this.mountSingle(req);
      }
      this.pushLiveStatus();
      this.hud?.setStatus({
        phase: "ready",
        tag: this.compareMode ? "A|B" : "run",
        elapsedSec: 0,
        caption: t("raid.sim.readyCaption"),
      });
    } catch (err) {
      setRaidAiSave({});
      clearSimMapOverride();
      this.activeStage = null;
      this.compareOffsetX = 0;
      this.restoreDefaultGameSize();
      this.setTransport("ready");
      this.hud?.setStatus({ phase: "ready", caption: String(err) });
      throw err;
    }
  }

  private handlePlay(req: SimCueRequest): void {
    if (simTransportPlayNeedsCue(this.transport) || this.lanes.length === 0) {
      this.cueReady(req);
    }
    if (this.lanes.length === 0) return;
    this.applyTransportEvent("play");
    this.hud?.setOutcome({ reports: [] });
    this.pushLiveStatus();
  }

  private handlePause(): void {
    if (this.transport !== "playing") return;
    this.applyTransportEvent("pause");
    this.pushLiveStatus();
  }

  /** Natural duration / combat end — freeze board with reports (not Stop cue). */
  private handleComplete(): void {
    if (this.lanes.length === 0) return;
    if (this.transport === "done" || this.transport === "ready") return;

    for (const lane of this.lanes) {
      if (!lane.report) {
        lane.report = finishSimRun(lane.ctx, {
          clearSave: false,
          clearMap: false,
        });
        lane.done = true;
      }
      lane.view.sync(lane.ctx.state, drainEvents(lane.ctx.state));
    }
    // Keep map/save until next cue so the final board stays painted.
    this.applyTransportEvent("complete");
    const reports = this.lanes.map((l) => l.report!).filter(Boolean);

    if (this.compareMode && reports.length >= 2) {
      const { summary } = compareReports(reports[0]!, reports[1]!);
      this.hud?.setOutcome({ reports, summary });
      this.hud?.setStatus({
        phase: "done",
        tag: "A|B",
        elapsedSec: Math.max(...this.lanes.map((l) => l.ctx.state.time)),
        caption: summary[0] ?? t("raid.sim.done"),
      });
    } else if (reports.length >= 1) {
      this.hud?.setOutcome({ reports });
      const m = reports[0]!.metrics;
      this.hud?.setStatus({
        phase: "done",
        tag: "run",
        elapsedSec: this.lanes[0]!.ctx.state.time,
        caption: t("raid.sim.doneCaption", {
          lag: formatDisplay(m.dodgeReactionLagSec ?? 0, 1),
          danger: formatDisplay(m.timeInDangerZone ?? 0, 1),
        }),
      });
    } else {
      this.hud?.setStatus({ phase: "done" });
    }
  }

  private mountSingle(req: SimCueRequest): void {
    this.compareMode = false;
    const ctx = prepareSimRun(req.a.scenarioId, req.a.patch);
    const stage = ctx.stage;
    this.activeStage = stage;
    this.compareOffsetX = 0;
    this.applyGameSize(stage.width, stage.height);
    this.resetCameras();
    this.time.delayedCall(0, () => {
      if (this.disposed) return;
      this.frameCamerasToHudGap(stage);
    });

    const view = new RaidPresentation(this, { stage });
    view.create(ctx.state);
    view.sync(ctx.state, []);
    this.addReadMarkers(ctx, 0);
    this.lanes = [{ tag: "run", ctx, view, done: false, report: null }];
    this.frameCamerasToHudGap(stage);
  }

  private mountCompare(req: SimCueRequest): void {
    this.compareMode = true;
    const ctxA = prepareSimRun(req.a.scenarioId, req.a.patch);
    const ctxB = prepareSimRun(req.b.scenarioId, req.b.patch);
    const stage = ctxA.stage;
    const gap = this.laneGap();
    const ox = stage.width + gap;
    this.activeStage = stage;
    this.compareOffsetX = ox;
    this.applyGameSize(stage.width * 2 + gap, stage.height);
    this.setupSplitCameras(stage, ox);
    this.time.delayedCall(0, () => {
      if (this.disposed) return;
      this.frameCamerasToHudGap(stage, { compare: true, offsetX: ox });
    });

    const viewA = new RaidPresentation(this, { stage });
    const viewB = new RaidPresentation(this, { offsetX: ox, stage });
    viewA.create(ctxA.state);
    viewB.create(ctxB.state);
    viewA.sync(ctxA.state, []);
    viewB.sync(ctxB.state, []);
    if (this.camB) {
      viewB.ignoreFromCamera(this.cameras.main);
      viewA.ignoreFromCamera(this.camB);
    }
    this.addReadMarkers(ctxA, 0, { hideFrom: this.camB });
    this.addReadMarkers(ctxB, ox, { hideFrom: this.cameras.main });
    this.lanes = [
      { tag: "A", ctx: ctxA, view: viewA, done: false, report: null },
      { tag: "B", ctx: ctxB, view: viewB, done: false, report: null },
    ];
    this.frameCamerasToHudGap(stage, { compare: true, offsetX: ox });
  }

  /** Each lane gets full stageW×stageH viewport at zoom 1 (never crop to half of 540). */
  private setupSplitCameras(stage: SimStageDef, offsetX: number): void {
    this.resetCameras();
    const stageW = stage.width;
    const stageH = stage.height;
    const gap = this.laneGap();
    const zoom = 1;

    this.cameras.main.setViewport(0, 0, stageW, stageH);
    this.cameras.main.setZoom(zoom);
    this.cameras.main.centerOn(stageW / 2, stageH / 2);
    this.cameras.main.setBackgroundColor("#0c1014");

    this.camB = this.cameras.add(stageW + gap, 0, stageW, stageH);
    this.camB.setZoom(zoom);
    this.camB.centerOn(offsetX + stageW / 2, stageH / 2);
    this.camB.setBackgroundColor("#0c1014");

    this.divider = this.add
      .rectangle(stageW + gap / 2, stageH / 2, 3, stageH, 0xffd27a, 0.85)
      .setScrollFactor(0)
      .setDepth(10_000);
    this.camB.ignore(this.divider);

    const labelStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: "Malgun Gothic, sans-serif",
      fontSize: "16px",
      color: "#ffd27a",
      backgroundColor: "#0a1220cc",
      padding: { x: 6, y: 2 },
    };
    const labA = this.add
      .text(12, 12, "A", labelStyle)
      .setScrollFactor(0)
      .setDepth(10_001);
    const labB = this.add
      .text(12, 12, "B", labelStyle)
      .setScrollFactor(0)
      .setDepth(10_001);
    this.cameras.main.ignore(labB);
    this.camB.ignore(labA);
    this.laneLabels = [labA, labB];
  }

  private resetCameras(): void {
    for (const lab of this.laneLabels) lab.destroy();
    this.laneLabels = [];
    this.divider?.destroy();
    this.divider = null;
    if (this.camB) {
      try {
        this.cameras?.remove?.(this.camB, true);
      } catch {
        /* scene systems may already be down */
      }
      this.camB = null;
    }
    const main = this.cameras?.main;
    if (!main) return;
    const { width, height } = this.scale;
    main.setViewport(0, 0, width, height);
    main.setZoom(1);
    main.setScroll(0, 0);
    main.setBackgroundColor("#0c1014");
  }

  private pushLiveStatus(): void {
    if (this.lanes.length === 0) return;
    const elapsed = Math.max(...this.lanes.map((l) => l.ctx.state.time));
    const phase = this.transport;
    if (this.compareMode && this.lanes.length >= 2) {
      const a = this.lanes[0]!;
      const b = this.lanes[1]!;
      const tokenA = liveMetricToken(a.ctx);
      const tokenB = liveMetricToken(b.ctx);
      const dual = t("raid.sim.liveCaptionDual", {
        a: behaviorLabel(a.ctx.focus.brain.select.activeId),
        b: behaviorLabel(b.ctx.focus.brain.select.activeId),
      });
      const metricBit =
        tokenA || tokenB ? ` · A ${tokenA || "—"} | B ${tokenB || "—"}` : "";
      this.hud?.setStatus({
        phase,
        tag: "A|B",
        elapsedSec: elapsed,
        activeId: a.ctx.focus.brain.select.activeId,
        caption:
          phase === "ready"
            ? t("raid.sim.readyCaption")
            : `${dual}${metricBit}`,
      });
      return;
    }
    const lane = this.lanes[0]!;
    this.hud?.setStatus({
      phase,
      tag: lane.tag,
      elapsedSec: lane.ctx.state.time,
      activeId: lane.ctx.focus.brain.select.activeId,
      caption:
        phase === "ready"
          ? t("raid.sim.readyCaption")
          : this.liveCaptionFor(lane),
    });
  }

  private abortPlayback(opts?: { keepTransport?: boolean }): void {
    this.clearReadMarkers();
    for (const lane of this.lanes) {
      try {
        if (!lane.report) {
          finishSimRun(lane.ctx, { clearSave: false, clearMap: false });
        }
      } catch {
        /* ignore */
      }
      try {
        lane.view.destroy();
      } catch {
        /* ignore */
      }
    }
    this.lanes = [];
    this.activeStage = null;
    this.compareOffsetX = 0;
    setRaidAiSave({});
    clearSimMapOverride();
    this.compareMode = false;
    if (!this.disposed && this.cameras?.main) {
      this.resetCameras();
      this.restoreDefaultGameSize();
    }
    if (!opts?.keepTransport) {
      this.setTransport("ready");
    }
  }

  private backToHub(): void {
    this.abortPlayback();
    assertTransition(SceneKey.RaidSim, SceneKey.RaidHub);
    this.scene.start(SceneKey.RaidHub);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.scale.off("resize", this.onScaleResize, this);
    this.abortPlayback();
    this.hud?.destroy();
    this.hud = null;
  }
}
